import {
  prisma,
  CheckInOutType,
  OrderStatus,
  OrderItemStatus,
  CheckInOutFrom,
  CheckInRules,
} from "client";
import { getCacheClient, writeCache, deleteCache } from "@sajilni/cache";
import moment from "moment-timezone";
import {
  CHECK_IN_OUT_LOCK_TTL_SECONDS,
  CHECKIN_ROSTER_TTL_SECONDS,
  checkinRosterKey,
  checkinRulesKey,
} from "@sajilni/config/constants";
import {
  readCheckinRosterEntry,
  readCheckinRulesForZone,
} from "./warmCheckinCache";
import { resolveAttendeeReportOrderItemIds } from "../attendeeReport";
import { isMongoObjectId, normalizeScanQuery } from "@sajilni/utils/ticketQr";
import {
  checkInCountWhere,
  checksInOutHotPathQuery,
  hasCheckInInMemory,
  mapWithConcurrency,
  needsZoneHistoryFallback,
  readBulkCheckinConcurrencyFromEnv,
} from "./hotPathLimits";
export * from "./getCheckInOutLogs";
export * from "./warmCheckinCache";
export * from "./hotPathLimits";
const CONCURRENT_CHECK_IN_OUT_ERROR =
  "Concurrent check-in/out in progress. Please retry.";
export const checkIfThereIsAnyZoneOnThisEvent = async (eventId: string) => {
  const zones = await prisma.zone.count({
    where: {
      venue: {
        eventIds: { has: eventId },
      },
      OR: [{ deletedAt: null }, { deletedAt: { isSet: false } }],
    },
  });
  return zones > 0;
};

export const getType = (orderItem: any, requireCheckout: boolean) => {
  const length = orderItem.checksInOut?.length;
  if (orderItem.checksInOut?.length === 0 || !requireCheckout) {
    return CheckInOutType.CHECK_IN;
  }

  const lastCheckInOut =
    orderItem.checksInOut[orderItem.checksInOut.length - 1];

  if (lastCheckInOut?.type === CheckInOutType.CHECK_IN) {
    return CheckInOutType.CHECK_OUT;
  }
  if (lastCheckInOut?.type === CheckInOutType.CHECK_OUT) {
    return CheckInOutType.CHECK_IN;
  }
  return CheckInOutType.CHECK_IN;
};
export const getLastCheckInOut = (orderItem: any) => {
  if (!orderItem?.checksInOut || orderItem.checksInOut.length === 0) {
    return null;
  }

  return orderItem.checksInOut[orderItem.checksInOut.length - 1];
};

export const isThereCheckInForThisOrderItem = async (orderItemId: any) => {
  return orderItemId.checksInOut.some(
    (checkIn: any) => checkIn.type === CheckInOutType.CHECK_IN
  );
};

const getDefaultEventCheckInRule = async (
  eventId: string
): Promise<CheckInRules | null> => {
  return prisma.checkInRules.findFirst({
    where: {
      eventId,
      isDefault: true,
      OR: [{ deletedAt: null }, { deletedAt: { isSet: false } }],
    },
  });
};

const getZoneCheckInRule = async (
  zoneId: string
): Promise<CheckInRules | null> => {
  return prisma.checkInRules.findFirst({
    where: {
      zonesIds: { has: zoneId },
      OR: [{ deletedAt: null }, { deletedAt: { isSet: false } }],
    },
  });
};

const getApplicableCheckInRule = async (
  eventId: string,
  zoneId?: string
): Promise<{ rule: CheckInRules | null; scope: "zone" | "event" }> => {
  if (zoneId) {
    const zoneRule = await getZoneCheckInRule(zoneId);
    if (zoneRule) return { rule: zoneRule, scope: "zone" };
  }
  const eventRule = await getDefaultEventCheckInRule(eventId);
  return { rule: eventRule, scope: "event" };
};

const checkInOutTimestamp = (row: {
  time?: Date;
  createdAt: Date;
}): Date => row.time ?? row.createdAt;

const getLastCheckInTime = (
  checksInOut: Array<{
    type: CheckInOutType;
    createdAt: Date;
    time?: Date;
    zoneId?: string;
  }>,
  zoneId?: string
): Date | undefined => {
  // Find the most recent CHECK_IN (optionally scoped to zone)
  const last = [...checksInOut]
    .filter(
      (c) =>
        c.type === CheckInOutType.CHECK_IN && (!zoneId || c.zoneId === zoneId)
    )
    .sort(
      (a, b) =>
        checkInOutTimestamp(b).getTime() - checkInOutTimestamp(a).getTime()
    )[0];
  return last ? checkInOutTimestamp(last) : undefined;
};

const getLastCheckInOutForScope = (
  checksInOut: Array<{
    type: CheckInOutType;
    createdAt: Date;
    time?: Date;
    zoneId?: string;
  }>,
  zoneId?: string
) => {
  const last = [...checksInOut]
    .filter((c) => (!zoneId ? true : c.zoneId === zoneId))
    .sort(
      (a, b) =>
        checkInOutTimestamp(b).getTime() - checkInOutTimestamp(a).getTime()
    )[0];
  return last || null;
};

const countCheckInsForScope = (orderItemId: string, zoneId?: string) =>
  prisma.checkInOut.count({
    where: checkInCountWhere(orderItemId, zoneId),
  });

const loadChecksInOutForScope = async <
  T extends { zoneId?: string | null },
>(
  orderItemId: string,
  checksInOut: T[],
  zoneId?: string
): Promise<T[]> => {
  if (!needsZoneHistoryFallback(checksInOut, zoneId) || !zoneId) {
    return checksInOut;
  }
  const zoneRows = await prisma.checkInOut.findMany({
    where: { orderItemId, zoneId },
    ...checksInOutHotPathQuery(),
  });
  return [...checksInOut, ...(zoneRows as unknown as T[])];
};

const hasCheckInForScope = async (
  orderItemId: string,
  checksInOut: Array<{ type: string; zoneId?: string | null }>,
  zoneId?: string
): Promise<boolean> => {
  if (hasCheckInInMemory(checksInOut, zoneId)) {
    return true;
  }
  const found = await prisma.checkInOut.findFirst({
    where: checkInCountWhere(orderItemId, zoneId),
    select: { id: true },
  });
  return Boolean(found);
};

export const findConfirmedOrderItemIdsForEvent = async (
  eventId: string
): Promise<string[]> => {
  const orderItems = await prisma.orderItem.findMany({
    where: {
      order: {
        status: OrderStatus.COMPLETED,
        eventId,
      },
      status: OrderItemStatus.CONFIRMED,
    },
    select: { id: true },
  });
  return orderItems.map((orderItem) => orderItem.id);
};

/**
 * Helper function to create a CheckInOutFailure record
 * Silently handles errors to prevent failure creation from breaking the main flow
 */
const createCheckInOutFailure = async (
  eventId: string,
  orderItemId: string,
  reason: string,
  type: CheckInOutType,
  createdById: string,
  zoneId?: string
) => {
  try {
    await prisma.checkInOutFailure.create({
      data: {
        eventId,
        orderItemId,
        reason,
        type,
        zoneId: zoneId || undefined,
        createdById,
      },
    });
  } catch (error) {
    // Silently log but don't throw - failure creation shouldn't break the main flow
    console.error("Failed to create CheckInOutFailure:", error);
  }
};

/**
 * Helper function to handle errors and create failure records
 * Returns the error object for consistent error handling
 */
const handleCheckInOutError = async (
  errorMessage: string,
  eventId: string,
  orderItemId: string,
  type: CheckInOutType,
  createdById: string,
  zoneId?: string
) => {
  await createCheckInOutFailure(
    eventId,
    orderItemId,
    errorMessage,
    type,
    createdById,
    zoneId
  );
  return { error: errorMessage };
};

/**
 * Distributed-lock helpers for check-in/out mutations.
 *
 * Pattern (per team alignment):
 *   1. Live validation runs every time (never skipped, never cached).
 *   2. A short-lived Redis lock (SET NX EX 5) per orderItemId prevents
 *      two simultaneous scans of the same ticket.
 *   3. The DB performs natural idempotency (state pre-check) so a
 *      replayed scan after the desired state is reached returns success
 *      without writing again.
 *
 * The lock key is keyed only on orderItemId because that's the unique
 * resource being mutated. Each acquisition uses a unique token so we
 * can release the lock atomically (CAS) and never free a lock another
 * worker has since taken after our TTL expired.
 */
/**
 * Logical (un-prefixed) lock key. The cache client prepends
 * `sajilni:{getCacheEnv()}`, so the actual Redis key is
 * `sajilni:{env}:lock:checkInOut:${orderItemId}`,
 * e.g. `sajilni:staging:lock:checkInOut:65a1b2c3...`.
 */
const checkInOutLockKey = (orderItemId: string) =>
  `lock:checkInOut:${orderItemId}`;

const generateLockToken = (ownerId: string): string =>
  `${ownerId}:${Date.now()}:${Math.random().toString(36).slice(2)}`;

type LockHandle = {
  acquired: boolean;
  /** Logical (un-prefixed) Redis key for log correlation. */
  key: string;
  release: () => Promise<void>;
};

const acquireCheckInOutLock = async (
  orderItemId: string,
  ownerId: string
): Promise<LockHandle> => {
  const cache = getCacheClient();
  const key = checkInOutLockKey(orderItemId);

  if (!cache) {
    console.warn(
      `[checkInOutLock] Redis not configured — running WITHOUT distributed lock for ${key}. Set UPSTASH_REDIS_REST_URL/TOKEN to enable.`
    );
    return { acquired: true, key, release: async () => { } };
  }

  const token = generateLockToken(ownerId);
  const acquireStart = Date.now();
  const acquired = await cache.setIfNotExists(key, token, {
    ttlSeconds: CHECK_IN_OUT_LOCK_TTL_SECONDS,
  });

  if (acquired) {
    console.log(
      `[checkInOutLock] ACQUIRED key=${key} token=${token} ttl=${CHECK_IN_OUT_LOCK_TTL_SECONDS}s owner=${ownerId}`
    );
  } else {
    console.warn(
      `[checkInOutLock] CONFLICT key=${key} owner=${ownerId} — another worker holds the lock (TTL up to ${CHECK_IN_OUT_LOCK_TTL_SECONDS}s)`
    );
  }

  return {
    acquired,
    key,
    release: async () => {
      if (!acquired) return;
      const heldFor = Date.now() - acquireStart;
      try {
        const removed = await cache.deleteIfValueMatches(key, token);
        console.log(
          `[checkInOutLock] RELEASED key=${key} token=${token} heldForMs=${heldFor} removed=${removed}`
        );
      } catch (err) {
        // Best-effort; TTL still releases the lock.
        console.warn(
          `[checkInOutLock] release failed key=${key} heldForMs=${heldFor} err=${err instanceof Error ? err.message : String(err)
          } — TTL will reclaim within ${CHECK_IN_OUT_LOCK_TTL_SECONDS}s`
        );
      }
    },
  };
};

/**
 * Wraps a check-in/out mutation in the unified lock pattern. The
 * generic `T` is inferred from `fn` only, so each conflict response
 * just needs to be assignable to the body's return type.
 */
async function withCheckInOutLock<T>(
  orderItemId: string,
  ownerId: string,
  onConflict: () => NoInfer<T>,
  fn: () => Promise<T>
): Promise<T> {
  const lock = await acquireCheckInOutLock(orderItemId, ownerId);
  if (!lock.acquired) return onConflict();
  try {
    return await fn();
  } finally {
    await lock.release();
  }
}

// CREATE
export const createCheckInByOrderItemId = async (
  orderItemId: string,
  eventId: string,
  createdById: string,
  selectedDate?: Date,
  zoneId?: string,
  autoCheckOut?: boolean,
  from?: CheckInOutFrom
) => {
  // validations
  // check if order item exists
  // event date < check in time
  // check order item chechinout array as follows:
  // to checkout you need to check in first
  // id day ends checkout by default
  // checkout > check in
  // no check ins  on sequence or checkout on sequence
  return withCheckInOutLock(
    orderItemId,
    createdById,
    () => ({ error: CONCURRENT_CHECK_IN_OUT_ERROR }),
    async () => {
      try {
        if ((await checkIfThereIsAnyZoneOnThisEvent(eventId)) && !zoneId) {
          return await handleCheckInOutError(
            "You have to select a zone",
            eventId,
            orderItemId,
            CheckInOutType.CHECK_IN,
            createdById,
            zoneId
          );
        }
        const event = await prisma.event.findUnique({
          where: { id: eventId },
        });
        let isAutoCheckout = autoCheckOut;
        const orderItem = await prisma.orderItem.findUnique({
          where: { id: orderItemId },
          include: {
            ticket: { select: { bookableItem: { select: { id: true } } } },
            invitationAttendee: {
              select: {
                id: true,
                contactId: true,
                invitation: { select: { id: true } },
              },
            },
            order: {
              select: {
                id: true,
                profileId: true,
                eventId: true,
                profile: {
                  select: {
                    id: true,
                    participantGroups: {
                      select: {
                        id: true,
                      },
                    },
                  },
                },
              },
            },
            checksInOut: checksInOutHotPathQuery(),
          },
        });

        if (!event || !orderItem || !event.startDate) {
          return await handleCheckInOutError(
            "Event or order item not found",
            eventId,
            orderItemId,
            CheckInOutType.CHECK_IN,
            createdById,
            zoneId
          );
        }
        const currentTime = moment()
          .tz(event?.timezone || "UTC")
          .toDate();

        if (event.endDate && currentTime > event.endDate) {
          return await handleCheckInOutError(
            "Event date has passed",
            eventId,
            orderItemId,
            CheckInOutType.CHECK_IN,
            createdById,
            zoneId
          );
        }

        let numberOfCheckins = 0;
        let totalScansAllowed: number | null = null;
        let requireCheckout = false;

        const { rule: applicableRule, scope } = await getApplicableCheckInRule(
          eventId,
          zoneId
        );

        if (zoneId) {
          // Desk CHECK_IN only — do not add this lookup to scanQr.
          const zone = await prisma.zone.findUnique({
            where: { id: zoneId },
          });
          if (!zone) {
            return await handleCheckInOutError(
              "Zone not found",
              eventId,
              orderItemId,
              CheckInOutType.CHECK_IN,
              createdById,
              zoneId
            );
          }
          if (zone.enableZoneAccessControl) {
            const accessibleTickets = zone.accessibleBookableItemIds;
            const orderItemTicket = orderItem?.ticket?.bookableItem?.id;

            const accessibleParticipantGroups = zone.accessibleUserGroupIds;
            const orderItemUser =
              orderItem?.order?.profile?.participantGroups || [];
            const accessibleInvitations = zone.accessibleInvitationIds;
            const orderItemInvitation =
              orderItem?.invitationAttendee?.invitation?.id;
            // NOTE: access-control checks remain zone-based; rules fallback can be event default
            // Check-in counts for scan limits are loaded via count() below — not from this window.

            // Check if user has access through either ticket type or user group
            const hasTicketAccess =
              zone.enableByTicketType &&
              orderItemTicket &&
              accessibleTickets.includes(orderItemTicket);
            const hasUserGroupAccess =
              zone.enableByUserType &&
              accessibleParticipantGroups.some((group) =>
                orderItemUser.some((user: any) => user.id === group)
              );
            const hasInvitationAccess =
              zone.enableByInvitation &&
              orderItemInvitation &&
              accessibleInvitations.includes(orderItemInvitation);

            // Allow access if either condition is met
            if (
              !hasTicketAccess &&
              !hasUserGroupAccess &&
              !hasInvitationAccess
            ) {
              const reasons = [];
              if (zone.enableByTicketType && !hasTicketAccess) {
                reasons.push("your ticket type does not grant access to this zone");
              }
              if (zone.enableByUserType && !hasUserGroupAccess) {
                reasons.push(
                  "you are not part of an authorized participant group"
                );
              }
              if (zone.enableByInvitation && !hasInvitationAccess) {
                reasons.push("you do not have a valid invitation");
              }

              return await handleCheckInOutError(
                `Access denied: ${reasons.join(", ")}.`,
                eventId,
                orderItemId,
                CheckInOutType.CHECK_IN,
                createdById,
                zoneId
              );
            }
          }
        }

        const scopeZoneId = scope === "zone" ? zoneId : undefined;
        const checksInOut = await loadChecksInOutForScope(
          orderItemId,
          orderItem.checksInOut as any,
          scopeZoneId
        );

        // Apply scan/rate/checkout settings using zone rule if exists, otherwise event default
        if (applicableRule) {
          if (applicableRule.validationStart && applicableRule.validationEnd) {
            if (
              currentTime < applicableRule.validationStart ||
              currentTime > applicableRule.validationEnd
            ) {
              return await handleCheckInOutError(
                `Access denied: Validation is only allowed between ${applicableRule.validationStart.toLocaleString()} and ${applicableRule.validationEnd.toLocaleString()}.`,
                eventId,
                orderItemId,
                CheckInOutType.CHECK_IN,
                createdById,
                zoneId
              );
            }
          }

          // NOTE on natural DB idempotency:
          //   The "already checked in → no-op success" check intentionally
          //   lives later in this function (inside the `requireCheckout`
          //   branch). Returning a no-op here would bypass legitimate
          //   multi-scan rules (`numberOfScans`, `minsBetweenScans`).
          //   Rapid duplicate scans are still suppressed by the
          //   RAPID_DEDUPE_MS check at the end of validation.

          const lastCheckinTime = getLastCheckInTime(
            checksInOut as any,
            scopeZoneId
          );

          if (
            applicableRule.minsBetweenScans &&
            applicableRule.minsBetweenScans > 0
          ) {
            if (
              lastCheckinTime &&
              currentTime <
              new Date(
                lastCheckinTime.getTime() +
                applicableRule.minsBetweenScans * 60000
              )
            ) {
              return await handleCheckInOutError(
                `You need to wait ${applicableRule.minsBetweenScans} minutes before checking in again`,
                eventId,
                orderItemId,
                CheckInOutType.CHECK_IN,
                createdById,
                zoneId
              );
            }
          }

          if (
            applicableRule.numberOfScans &&
            applicableRule.numberOfScans > 0
          ) {
            totalScansAllowed = applicableRule.numberOfScans;
            numberOfCheckins = await countCheckInsForScope(
              orderItemId,
              scopeZoneId
            );
            if (numberOfCheckins >= applicableRule.numberOfScans) {
              return await handleCheckInOutError(
                `You have reached the maximum number of checkins: ${numberOfCheckins}/${totalScansAllowed}`,
                eventId,
                orderItemId,
                CheckInOutType.CHECK_IN,
                createdById,
                zoneId
              );
            }
          }

          requireCheckout = applicableRule.isCheckoutRequired || false;
          if (requireCheckout && checksInOut.length > 0) {
            const lastForScope = getLastCheckInOutForScope(
              checksInOut as any,
              scopeZoneId
            );
            // If checkout is required and we're already checked in for this scope,
            // this is the desired state; treat repeated CHECK_IN as a no-op.
            if (lastForScope?.type === CheckInOutType.CHECK_IN) {
              const prevMsg =
                typeof (lastForScope as { message?: string }).message ===
                  "string"
                  ? (lastForScope as { message?: string }).message
                  : undefined;
              return {
                success:
                  prevMsg ??
                  `Order item ${orderItemId} already checked in (no-op)`,
              };
            }
          }
        }

        const RAPID_DEDUPE_MS = 2500;
        const lastForDedupe = getLastCheckInOutForScope(
          checksInOut as any,
          scopeZoneId
        );
        const lastForDedupeAt = lastForDedupe
          ? checkInOutTimestamp(lastForDedupe as any)
          : null;
        if (
          lastForDedupe?.type === CheckInOutType.CHECK_IN &&
          lastForDedupeAt &&
          currentTime.getTime() - lastForDedupeAt.getTime() < RAPID_DEDUPE_MS
        ) {
          const prevMsg =
            typeof (lastForDedupe as { message?: string }).message === "string"
              ? (lastForDedupe as { message?: string }).message
              : undefined;
          return {
            success:
              prevMsg ??
              `Order item ${orderItemId} checked in (duplicate suppressed)`,
          };
        }

        // Increment the count before creating check-in
        numberOfCheckins += 1;

        // Build success message with scan count if there's a limit
        const formatedDate = moment(currentTime)
          .tz(event?.timezone)
          .format("YYYY-MM-DD HH:mm:ss Z");
        let defaultSuccessMessage = `Order item ${orderItemId} checked in at ${formatedDate}`;
        if (totalScansAllowed !== null) {
          defaultSuccessMessage = `Order item ${orderItemId} checked in (${numberOfCheckins}/${totalScansAllowed} scans) at ${formatedDate}`;
        }
        const successMessage = defaultSuccessMessage;

        const created = await prisma.checkInOut.create({
          data: {
            orderItem: { connect: { id: orderItemId } },
            type: CheckInOutType.CHECK_IN,
            time: currentTime,
            createdBy: { connect: { id: createdById } },
            zone: zoneId ? { connect: { id: zoneId } } : undefined,
            autoCheckOut: isAutoCheckout,
            message: successMessage,
          },
          select: { id: true },
        });

        return {
          success: successMessage,
          createdCheckIn: {
            id: created.id,
            eventId,
            profileId:
              orderItem.invitationAttendee?.contactId ??
              orderItem.order.profileId ??
              orderItem.order.profile?.id ??
              null,
          },
        };
      } catch (error: any) {
        console.error("Create check-in error:", error);
        await createCheckInOutFailure(
          eventId,
          orderItemId,
          error?.message || "Unknown error occurred",
          CheckInOutType.CHECK_IN,
          createdById,
          zoneId
        );
        throw error;
      }
    }
  );
};

//create checkout
export const createCheckOutByOrderItemId = async (
  orderItemId: string,
  eventId: string,
  createdById: string,
  selectedDate?: Date,
  zoneId?: string,
  autoCheckOut?: boolean,
  from?: CheckInOutFrom
) => {
  return withCheckInOutLock(
    orderItemId,
    createdById,
    () => ({ error: CONCURRENT_CHECK_IN_OUT_ERROR }),
    async () => {
      try {
        if ((await checkIfThereIsAnyZoneOnThisEvent(eventId)) && !zoneId) {
          return await handleCheckInOutError(
            "You have to select a zone",
            eventId,
            orderItemId,
            CheckInOutType.CHECK_OUT,
            createdById,
            zoneId
          );
        }
        const event = await prisma.event.findUnique({
          where: { id: eventId },
        });
        const currentTime = moment()
          .tz(event?.timezone || "UTC")
          .toDate();
        let isAutoCheckout = autoCheckOut;
        const orderItem = await prisma.orderItem.findUnique({
          where: { id: orderItemId },
          include: {
            checksInOut: checksInOutHotPathQuery(),
          },
        });

        if (!event || !orderItem || !event.startDate) {
          return await handleCheckInOutError(
            "Event or order item not found",
            eventId,
            orderItemId,
            CheckInOutType.CHECK_OUT,
            createdById,
            zoneId
          );
        }
        //comment it because we should not check this because we allow checkout after event end date
        // if (event.endDate && currentTime > event.endDate) {
        //   return await handleCheckInOutError(
        //     "Event date has passed",
        //     eventId,
        //     orderItemId,
        //     CheckInOutType.CHECK_OUT,
        //     createdById,
        //     zoneId
        //   );
        // }
        const { rule: applicableRule, scope } = await getApplicableCheckInRule(
          eventId,
          zoneId
        );

        const scopeZoneId = scope === "zone" ? zoneId : undefined;
        const checksInOut = await loadChecksInOutForScope(
          orderItemId,
          orderItem.checksInOut as any,
          scopeZoneId
        );

        // Enforce "Checkout without check-in is not allowed" only when configured by rule
        const requireCheckout = applicableRule?.isCheckoutRequired || false;
        if (requireCheckout) {
          const hasPriorCheckIn = await hasCheckInForScope(
            orderItemId,
            checksInOut as any,
            scopeZoneId
          );
          if (!hasPriorCheckIn) {
            return await handleCheckInOutError(
              "Checkout without check-in is not allowed",
              eventId,
              orderItemId,
              CheckInOutType.CHECK_OUT,
              createdById,
              zoneId
            );
          }
        }

        const lastCheckInOut = getLastCheckInOutForScope(
          checksInOut as any,
          scopeZoneId
        );
        const RAPID_DEDUPE_MS = 2500;
        if (lastCheckInOut?.type === CheckInOutType.CHECK_OUT) {
          const elapsed =
            currentTime.getTime() -
            checkInOutTimestamp(lastCheckInOut as any).getTime();
          if (elapsed >= 0 && elapsed < RAPID_DEDUPE_MS) {
            const prevMsg =
              typeof (lastCheckInOut as { message?: string }).message ===
                "string"
                ? (lastCheckInOut as { message?: string }).message
                : undefined;
            return {
              success:
                prevMsg ??
                `Order item ${orderItemId} checked out (duplicate suppressed)`,
            };
          }
          const prevMsg =
            typeof (lastCheckInOut as { message?: string }).message === "string"
              ? (lastCheckInOut as { message?: string }).message
              : undefined;
          return {
            success:
              prevMsg ??
              `Order item ${orderItemId} already checked out (no-op)`,
          };
        }
        // else if (!lastCheckInOut?.type) {
        //   return {
        //     error: "Order item is not checked in",
        //   };
        // }
        const formatedDate = moment(currentTime)
          .tz(event?.timezone)
          .format("YYYY-MM-DD HH:mm:ss Z");
        const successMessage = `Order item ${orderItemId} checked out at ${formatedDate}`;

        await prisma.checkInOut.create({
          data: {
            orderItem: { connect: { id: orderItemId } },
            type: CheckInOutType.CHECK_OUT,
            time: currentTime,
            createdBy: { connect: { id: createdById } },
            zone: zoneId ? { connect: { id: zoneId } } : undefined,
            autoCheckOut: isAutoCheckout,
            message: successMessage,
          },
        });

        return {
          success: successMessage,
        };
      } catch (error: any) {
        console.error("Create check-out error:", error);
        await createCheckInOutFailure(
          eventId,
          orderItemId,
          error?.message || "Unknown error occurred",
          CheckInOutType.CHECK_OUT,
          createdById,
          zoneId
        );
        throw error;
      }
    }
  );
};

export const resolveOrderItemIdForScan = async ({
  eventId,
  scanValue,
  isDTCMEvent = false,
  eventDoorsOpen = false,
}: {
  eventId: string;
  scanValue: string;
  isDTCMEvent?: boolean;
  eventDoorsOpen?: boolean;
}): Promise<string | null> => {
  const query = normalizeScanQuery(scanValue);
  if (!query || !eventId) {
    return null;
  }

  const confirmedOrderItemWhere = {
    status: OrderItemStatus.CONFIRMED,
    order: {
      eventId,
      status: OrderStatus.COMPLETED,
    },
  };

  const isObjectId = isMongoObjectId(query);

  if (isDTCMEvent) {
    const byBarcode = await prisma.orderItem.findFirst({
      where: {
        ...confirmedOrderItemWhere,
        dtcmBarcode: query,
      },
      select: { id: true },
    });
    if (byBarcode) {
      return byBarcode.id;
    }

    if (eventDoorsOpen && isObjectId) {
      const byId = await prisma.orderItem.findFirst({
        where: {
          ...confirmedOrderItemWhere,
          id: query,
        },
        select: { id: true },
      });
      if (byId) {
        return byId.id;
      }
    }

    return null;
  }

  if (!isObjectId) {
    return null;
  }

  const byId = await prisma.orderItem.findFirst({
    where: {
      ...confirmedOrderItemWhere,
      id: query,
    },
    select: { id: true },
  });

  return byId?.id ?? null;
};

export const scanQr = async (
  orderItemId: string,
  createdById: string,
  zoneId: string,
  autoCheckIn?: boolean,
  autoCheckOut?: boolean
) => {
  // Guard up-front so we fail with a clean, actionable error instead of a
  // Prisma "Argument `id` must not be null" deep inside the rules query.
  // Every downstream branch (rules lookup, checkInOut.create) requires a
  // real zoneId — there is no meaningful no-zone scan in the current flow.
  if (!orderItemId || typeof orderItemId !== "string") {
    throw new Error("scanQr: orderItemId is required");
  }
  if (!zoneId || typeof zoneId !== "string") {
    throw new Error("scanQr: zoneId is required");
  }

  return withCheckInOutLock(
    orderItemId,
    createdById,
    () => {
      throw new Error(CONCURRENT_CHECK_IN_OUT_ERROR);
    },
    async () => {
      try {
        // ── Cache-first parallel reads (both paths fail-open to DB) ─────────────
        const [checkInRules, liveOrderItem, cachedRoster] = await Promise.all([
          readCheckinRulesForZone(zoneId),
          prisma.orderItem.findUnique({
            where: { id: orderItemId },
            select: {
              scannedCount: true,
              invitationAttendee: { select: { contactId: true } },
              order: { select: { profileId: true, eventId: true } },
              checksInOut: checksInOutHotPathQuery(),
            },
          }),
          readCheckinRosterEntry(orderItemId),
        ]);

        if (!liveOrderItem) {
          throw new Error("Order item not found");
        }

        // ── Cold-cache fallback ────────────────────────────────────────────────────
        // If the roster wasn't pre-warmed (new registration after warm,
        // or Redis was unreachable during warm), do the same slim static
        // read scanQr used to do, then opportunistically (non-blocking)
        // write back. We swallow Redis errors here on purpose: scanning
        // must succeed even when Upstash is down.
        let roster = cachedRoster;
        if (!roster) {
          console.warn(
            `[checkinCache] scanQr ROSTER_FALLBACK orderItemId=${orderItemId} zoneId=${zoneId}`
          );
          const dbStatic = await prisma.orderItem.findUnique({
            where: { id: orderItemId },
            select: {
              ticket: {
                select: { id: true, bookableItem: { select: { id: true } } },
              },
              invitationAttendee: {
                select: { invitation: { select: { id: true } } },
              },
              order: {
                select: {
                  profile: {
                    select: { participantGroups: { select: { id: true } } },
                  },
                  event: {
                    select: { id: true, endDate: true, timezone: true },
                  },
                },
              },
            },
          });
          if (!dbStatic) {
            throw new Error("Order item not found");
          }
          roster = {
            orderItemId,
            eventId: dbStatic.order?.event?.id ?? "",
            ticketId: dbStatic.ticket?.id ?? null,
            bookableItemId: dbStatic.ticket?.bookableItem?.id ?? null,
            invitationId: dbStatic.invitationAttendee?.invitation?.id ?? null,
            participantGroupIds:
              dbStatic.order?.profile?.participantGroups?.map((g) => g.id) ??
              [],
            eventEndDateIso:
              dbStatic.order?.event?.endDate?.toISOString() ?? null,
            eventTimezone: dbStatic.order?.event?.timezone ?? null,
          };
          // Best-effort backfill — never block the scan on a Redis write.
          writeCache(checkinRosterKey(orderItemId), roster, {
            ttlSeconds: CHECKIN_ROSTER_TTL_SECONDS,
          }).catch((err) => {
            console.warn(
              `[checkinCache] scanQr ROSTER_BACKFILL_FAIL orderItemId=${orderItemId} err=${err instanceof Error ? err.message : String(err)
              }`
            );
          });
        }

        // ── Rebuild the shape the rest of scanQr expects ───────────────────────────
        // The downstream access-control, time-window, and check-in/out
        // creation logic below was written against the previous fat
        // `findUnique` shape. We adapt cached + live data back into that
        // shape so the rest of the function is unchanged.
        const orderItem = {
          scannedCount: liveOrderItem.scannedCount ?? 0,
          ticket: roster.ticketId
            ? {
              id: roster.ticketId,
              bookableItem: roster.bookableItemId
                ? { id: roster.bookableItemId }
                : null,
            }
            : null,
          invitationAttendee: roster.invitationId
            ? { invitation: { id: roster.invitationId } }
            : null,
          order: {
            profile: {
              participantGroups: roster.participantGroupIds.map((id) => ({
                id,
              })),
            },
            event: {
              id: roster.eventId,
              endDate: roster.eventEndDateIso
                ? new Date(roster.eventEndDateIso)
                : null,
              timezone: roster.eventTimezone,
            },
          },
          checksInOut: liveOrderItem.checksInOut,
        };

        // ── Access control ─────────────────────────────────────────────────────────
        const ruleZone = checkInRules
          .flatMap((r: any) => r.zones || [])
          .find((z: any) => z.id === zoneId);

        const allowedTicketIds = new Set<string>(
          (ruleZone?.accessibleTickets || []).map((t: any) => t.id)
        );
        const allowedInvitationIds = new Set<string>(
          (ruleZone?.accessibleInvitations || []).map((i: any) => i.id)
        );
        const allowedUserGroupIds = new Set<string>(
          (ruleZone?.accessibleUserGroups || []).map((g: any) => g.id)
        );

        const itemTicketId = (orderItem as any)?.ticket?.id;
        const itemInvitationId = (orderItem as any)?.invitationAttendee
          ?.invitation?.id;
        const itemUserGroupIds: string[] = (
          (orderItem as any)?.order?.profile?.participantGroups || []
        ).map((g: any) => g.id);

        const hasTicketAccess =
          allowedTicketIds.size > 0 && itemTicketId
            ? allowedTicketIds.has(itemTicketId)
            : false;
        const hasInvitationAccess =
          allowedInvitationIds.size > 0 && itemInvitationId
            ? allowedInvitationIds.has(itemInvitationId)
            : false;
        const hasUserGroupAccess =
          allowedUserGroupIds.size > 0 && itemUserGroupIds.length > 0
            ? itemUserGroupIds.some((id) => allowedUserGroupIds.has(id))
            : false;

        const hasDefinedConstraints =
          allowedTicketIds.size > 0 || allowedInvitationIds.size > 0;

        if (
          hasDefinedConstraints &&
          !(hasTicketAccess || hasInvitationAccess || hasUserGroupAccess)
        ) {
          throw new Error(
            "Access denied: This badge does not have access to this zone"
          );
        }

        // ── Scan count check ───────────────────────────────────────────────────────
        // `checkInRules` comes through getOrSetCache<CacheValue> and is
        // therefore widened to `any[]`. Annotate the lambdas explicitly
        // to match the rest of this file's `(x: any)` style and keep
        // strict-mode happy.
        const validScans = (checkInRules as any[])
          .map((rule: any) => rule.numberOfScans)
          .filter((n: any): n is number => typeof n === "number");
        const maxAllowedScans = Math.max(1, ...validScans);

        if ((orderItem.scannedCount ?? 0) >= maxAllowedScans) {
          throw new Error("Badge already scanned maximum number of times");
        }

        // ── Fetch the applicable rule ONCE (replaces two separate calls inside
        //    createCheckInByOrderItemId / createCheckOutByOrderItemId) ──────────────
        const eventId = orderItem.order.event.id;
        const { rule: applicableRule, scope } = await getApplicableCheckInRule(
          eventId,
          zoneId
        );

        const currentTime = moment()
          .tz(orderItem.order.event.timezone || "UTC")
          .toDate();

        if (
          orderItem.order.event.endDate &&
          currentTime > orderItem.order.event.endDate
        ) {
          throw new Error("Event date has passed");
        }

        const scopeZoneId = scope === "zone" ? zoneId : undefined;
        const scopedChecksInOut = await loadChecksInOutForScope(
          orderItemId,
          orderItem.checksInOut as any,
          scopeZoneId
        );

        // Local copy updated after each create so the autoCheckOut step sees the
        // CHECK_IN that autoCheckIn just wrote — no extra DB read required.
        const localChecksInOut: Array<{
          id: string;
          type: CheckInOutType;
          createdAt: Date;
          time?: Date;
          zoneId: string | null | undefined;
        }> = [...(scopedChecksInOut as any)];
        let createdCheckIn: {
          id: string;
          eventId: string;
          profileId: string | null;
        } | null = null;

        // ── Auto check-in ──────────────────────────────────────────────────────────
        if (autoCheckIn) {
          if (applicableRule) {
            if (
              applicableRule.validationStart &&
              applicableRule.validationEnd
            ) {
              if (
                currentTime < applicableRule.validationStart ||
                currentTime > applicableRule.validationEnd
              ) {
                throw new Error(
                  `Access denied: Validation is only allowed between ${applicableRule.validationStart.toLocaleString()} and ${applicableRule.validationEnd.toLocaleString()}.`
                );
              }
            }

            const lastCheckinTime = getLastCheckInTime(
              localChecksInOut as any,
              scopeZoneId
            );
            const numberOfCheckins =
              applicableRule.numberOfScans && applicableRule.numberOfScans > 0
                ? await countCheckInsForScope(orderItemId, scopeZoneId)
                : 0;

            if (
              applicableRule.minsBetweenScans &&
              applicableRule.minsBetweenScans > 0 &&
              lastCheckinTime &&
              currentTime <
              new Date(
                lastCheckinTime.getTime() +
                applicableRule.minsBetweenScans * 60000
              )
            ) {
              throw new Error(
                `You need to wait ${applicableRule.minsBetweenScans} minutes before checking in again`
              );
            }

            if (
              applicableRule.numberOfScans &&
              applicableRule.numberOfScans > 0 &&
              numberOfCheckins >= applicableRule.numberOfScans
            ) {
              throw new Error(
                `You have reached the maximum number of checkins: ${numberOfCheckins}/${applicableRule.numberOfScans}`
              );
            }

            if (
              applicableRule.isCheckoutRequired &&
              localChecksInOut.length > 0 &&
              getLastCheckInOutForScope(localChecksInOut as any, scopeZoneId)
                ?.type === CheckInOutType.CHECK_IN
            ) {
              throw new Error("You must check out before checking in again");
            }
          }

          const created = await prisma.checkInOut.create({
            data: {
              orderItem: { connect: { id: orderItemId } },
              type: CheckInOutType.CHECK_IN,
              time: currentTime,
              createdBy: { connect: { id: createdById } },
              zone: { connect: { id: zoneId } },
              autoCheckOut: autoCheckOut,
            },
            select: { id: true },
          });
          createdCheckIn = {
            id: created.id,
            eventId: liveOrderItem.order?.eventId ?? cachedRoster?.eventId ?? "",
            profileId:
              liveOrderItem.invitationAttendee?.contactId ??
              liveOrderItem.order?.profileId ??
              null,
          };

          // Reflect the new CHECK_IN in the local array so autoCheckOut can see it
          localChecksInOut.push({
            id: created.id,
            type: CheckInOutType.CHECK_IN,
            createdAt: currentTime,
            time: currentTime,
            zoneId,
          });
        }

        // ── Auto check-out ─────────────────────────────────────────────────────────
        if (autoCheckOut) {
          if (applicableRule?.isCheckoutRequired) {
            const hasPriorCheckIn = await hasCheckInForScope(
              orderItemId,
              localChecksInOut as any,
              scope === "zone" ? zoneId : undefined
            );
            if (!hasPriorCheckIn) {
              throw new Error("Checkout without check-in is not allowed");
            }
          }

          if (
            getLastCheckInOutForScope(
              localChecksInOut as any,
              scope === "zone" ? zoneId : undefined
            )?.type === CheckInOutType.CHECK_OUT
          ) {
            throw new Error("Order item is already checked out");
          }

          await prisma.checkInOut.create({
            data: {
              orderItem: { connect: { id: orderItemId } },
              type: CheckInOutType.CHECK_OUT,
              time: currentTime,
              createdBy: { connect: { id: createdById } },
              zone: { connect: { id: zoneId } },
              autoCheckOut: autoCheckOut,
            },
          });
        }

        // ── Mark as scanned ────────────────────────────────────────────────────────
        await prisma.orderItem.update({
          where: { id: orderItemId },
          data: { scanned: true, lastScannedAt: new Date() },
        });

        return { success: true, createdCheckIn };
      } catch (error) {
        console.error(
          `[checkinCache] scanQr FAIL orderItemId=${orderItemId} zoneId=${zoneId} err=${error instanceof Error ? error.message : String(error)
          }`
        );
        throw error;
      }
    }
  );
};
export const cancelLastCheckInOutByOrderItemId = async (
  orderItemId: string,
  createdById: string
) => {
  return withCheckInOutLock(
    orderItemId,
    createdById,
    () => ({ error: CONCURRENT_CHECK_IN_OUT_ERROR }),
    async () => {
      try {
        const orderItem = await prisma.orderItem.findUnique({
          where: { id: orderItemId },
          include: {
            checksInOut: {
              where: {
                type: CheckInOutType.CHECK_IN,
              },
              orderBy: { createdAt: "desc" },
              take: 1,
            },
          },
        });

        if (!orderItem?.checksInOut?.length) {
          throw new Error("No checkin or checkout found");
        }

        const lastCheckInOut = orderItem?.checksInOut?.[0];

        if (!lastCheckInOut) {
          throw new Error("No checkin or checkout found");
        }

        // if (lastCheckInOut.createdById !== createdById) {
        //   return { error: "You can only cancel your own checkin or checkout" };
        // } // if the checkin or checkout was not created by the user return error

        const checksInOut = await prisma.checkInOut.update({
          where: { id: lastCheckInOut.id },
          data: { deletedAt: new Date() },
        }); // delete the last checkin or checkout
        return {
          success: `Order item ${orderItemId} ${checksInOut.type} at ${checksInOut.time} has been canceled`,
        }; // return success message
      } catch (error: any) {
        return { error: error?.message || "An unexpected error occurred" };
      }
    }
  );
};

export const checkInOutMultiByOrderItemIds = async (
  orderItemIds: string[] | "ALL",
  eventId: string,
  type: CheckInOutType,
  createdById: string,
  zoneId?: string
) => {
  try {
    const event = await prisma.event.findUnique({
      where: { id: eventId },
    });

    if (!event || !event.startDate) {
      return { error: "Event or order item not found" };
    }
    const currentTime = moment()
      .tz(event?.timezone || "UTC")
      .toDate();

    if (event.endDate && currentTime > event.endDate) {
      return { error: "Event date has passed" };
    }
    if (orderItemIds === "ALL") {
      orderItemIds = await findConfirmedOrderItemIdsForEvent(eventId);
    }

    // Cap in-process fan-out. Queue/UI already chunk by BULK_CHECKIN_CAP;
    // this keeps a single service call from opening N fat check-in paths at once.
    const concurrency = readBulkCheckinConcurrencyFromEnv();
    const results = await mapWithConcurrency(
      orderItemIds,
      concurrency,
      async (orderItemId) => {
        try {
          if (type === CheckInOutType.CHECK_IN) {
            const result = await createCheckInByOrderItemId(
              orderItemId,
              eventId,
              createdById,
              undefined,
              zoneId,
              false
            );
            if ("error" in result && result.error) {
              throw new Error(result.error);
            }
            return {
              orderItemId,
              success: true,
              createdCheckIn:
                "createdCheckIn" in result ? result.createdCheckIn : undefined,
            };
          } else if (type === CheckInOutType.CHECK_OUT) {
            const result = await createCheckOutByOrderItemId(
              orderItemId,
              eventId,
              createdById,
              undefined,
              zoneId,
              false
            );
            if ("error" in result && result.error) {
              throw new Error(result.error);
            }
          }
          return { orderItemId, success: true };
        } catch (error: any) {
          // Save failure to database
          await prisma.checkInOutFailure.create({
            data: {
              eventId,
              orderItemId,
              reason: error?.message || "Unknown error occurred",
              type,
              zoneId,
              createdById,
            },
          });
          throw error;
        }
      }
    );

    const failures = results.filter((result) => result.status === "rejected");
    const successes = results.filter((result) => result.status === "fulfilled");
    const successfulOrderItemIds = orderItemIds.filter(
      (_, i) => results[i].status === "fulfilled"
    );
    const createdCheckIns = successes.flatMap((result) => {
      const value = result.status === "fulfilled" ? result.value : null;
      return value && "createdCheckIn" in value && value.createdCheckIn
        ? [value.createdCheckIn]
        : [];
    });

    if (failures.length > 0) {
      return {
        success: `${successes.length} orders checked ${type === CheckInOutType.CHECK_IN ? "in" : "out"
          } successfully`,
        failures: failures.map((failure: any, index) => ({
          orderItemId: orderItemIds[results.indexOf(failure)],
          reason: failure.reason?.message || "Unknown error",
        })),
        failureCount: failures.length,
        successCount: successes.length,
        successfulOrderItemIds,
        createdCheckIns,
      };
    }

    return {
      success: `${successes.length} orders checked ${type === CheckInOutType.CHECK_IN ? "in" : "out"
        } at ${currentTime}`,
      successCount: successes.length,
      failureCount: 0,
      successfulOrderItemIds,
      createdCheckIns,
    };
  } catch (error) {
    return { error };
  }
};

export const checkoutAllByEventId = async (
  userId: string,
  eventId: string,
  createdById: string
) => {
  // validations
  // check if order item exists
  // if last check  was check in

  try {
    const event = await prisma.event.findUnique({
      where: { id: eventId },
    });
    const currentTime = moment()
      .tz(event?.timezone || "UTC")
      .toDate();
    if (!event || !event.startDate || !event.endDate) {
      return { error: "Event not found" };
    }

    if (event.endDate && currentTime > event.endDate) {
      return { error: "Event date has passed" };
    }

    const orderItems = await prisma.orderItem.findMany({
      where: {
        order: {
          eventId,
        },
      },
      include: { checksInOut: true },
    });

    const checkInOuts = await Promise.all(
      orderItems.map(async (orderItem) => {
        const getType = () => {
          const length = orderItem.checksInOut.length;

          if (
            orderItem.checksInOut[length - 1]?.type === CheckInOutType.CHECK_IN
          ) {
            return CheckInOutType.CHECK_OUT;
          }
          return;
        };

        if (!getType()) {
          return;
        }
        const checksInOut = await prisma.checkInOut.create({
          data: {
            orderItem: { connect: { id: orderItem.id } },
            type: getType() as CheckInOutType,
            time: currentTime,
            createdBy: { connect: { id: createdById } },
          },
        });
        return checksInOut;
      })
    );

    return {
      success: `Checked out all order items ${checkInOuts.length} at ${currentTime}
    `,
    };
  } catch (error) {
    return { error };
  }
};
// Read
export const getCheckInOutByOrderItemId = async (
  userId: string,
  orderItemId: string
) => {
  try {
    const checksInOut = await prisma.checkInOut.findMany({
      where: { orderItemId },
    });
    return { checksInOut };
  } catch (error) {
    return { error };
  }
};

// Update
export const updateCheckInOut = async (
  userId: string,
  checkInOutId: string,
  type: CheckInOutType
) => {
  try {
    const checksInOut = await prisma.checkInOut.update({
      where: { id: checkInOutId },
      data: { type },
    });
    return { checksInOut };
  } catch (error) {
    return { error };
  }
};

// Delete
export const deleteCheckInOut = async (
  userId: string,
  checkInOutId: string
) => {
  try {
    const checksInOut = await prisma.checkInOut.delete({
      where: { id: checkInOutId },
    });
    return { checksInOut };
  } catch (error) {
    return { error };
  }
};

export const deleteAllCheckInOutByOrderItemId = async (
  userId: string,
  orderItemId: string
) => {
  return withCheckInOutLock(
    orderItemId,
    userId,
    () => ({ error: CONCURRENT_CHECK_IN_OUT_ERROR }),
    async () => {
      try {
        const checksInOut = await prisma.checkInOut.deleteMany({
          where: { orderItemId },
        });
        return { checksInOut };
      } catch (error) {
        return { error };
      }
    }
  );
};

// Helper function to determine the checksInOut condition
function getCheckInOutCondition(selectedDate?: Date, zoneId?: string) {
  if (selectedDate?.setHours) {
    return {
      where: {
        time: {
          gte: new Date(selectedDate?.setHours(0, 0, 0, 0) || ""),
          lte: new Date(selectedDate?.setHours(23, 59, 59, 999) || ""),
        },
      },
    };
  } else if (zoneId) {
    return {
      where: {
        zoneId,
      },
    };
  } else {
    return true;
  }
}

// Cron job service for automatic checkouts
export const autoCheckoutOrderItems = async () => {
  try {
    const currentTime = new Date();

    // Get all check-ins that need auto checkout
    const checkInsToProcess = await prisma.checkInOut.findMany({
      where: {
        type: CheckInOutType.CHECK_IN,
        autoCheckOut: true,
        orderItem: {
          order: {
            event: {
              OR: [
                {
                  endDate: {
                    not: null,
                    lt: currentTime,
                  },
                },
                {
                  AND: [
                    { endDate: null },
                    {
                      startDate: {
                        lt: new Date(
                          currentTime.getTime() - 24 * 60 * 60 * 1000
                        ),
                      },
                    },
                  ],
                },
              ],
            },
          },
        },
      },
      include: {
        orderItem: {
          include: {
            checksInOut: {
              orderBy: {
                time: "asc", // Order by time ascending to process in sequence
              },
            },
          },
        },
      },
      orderBy: {
        time: "asc", // Order by time ascending to process oldest first
      },
    });

    // Filter check-ins that need checkout
    const filteredCheckIns = checkInsToProcess.filter((checkIn) => {
      const orderItemCheckIns = checkIn.orderItem.checksInOut;

      // Get all check-ins and checkouts for this zone
      const zoneActivity = orderItemCheckIns.filter(
        (activity) => activity.zoneId === checkIn.zoneId
      );

      // Sort by time to ensure proper sequence
      zoneActivity.sort((a, b) => a.time.getTime() - b.time.getTime());

      // Find the index of current check-in
      const currentIndex = zoneActivity.findIndex(
        (activity) => activity.id === checkIn.id
      );

      if (currentIndex === -1) return false;

      // Look for a matching checkout after this check-in
      const hasMatchingCheckout = zoneActivity
        .slice(currentIndex + 1)
        .some(
          (activity) =>
            activity.type === CheckInOutType.CHECK_OUT &&
            activity.zoneId === checkIn.zoneId
        );

      // Count check-ins and checkouts up to this point
      const previousActivities = zoneActivity.slice(0, currentIndex + 1);
      const checkInsCount = previousActivities.filter(
        (a) => a.type === CheckInOutType.CHECK_IN
      ).length;
      const checkOutsCount = previousActivities.filter(
        (a) => a.type === CheckInOutType.CHECK_OUT
      ).length;

      // Return true if:
      // 1. No matching checkout exists after this check-in
      // 2. The number of check-ins is greater than checkouts (meaning this check-in needs a checkout)
      return !hasMatchingCheckout && checkInsCount > checkOutsCount;
    });

    // Create checkouts for each valid check-in
    const checkouts = await Promise.all(
      filteredCheckIns.map(async (checkIn) => {
        return prisma.checkInOut.create({
          data: {
            orderItemId: checkIn.orderItemId,
            type: CheckInOutType.CHECK_OUT,
            time: currentTime,
            zoneId: checkIn.zoneId,
            autoCheckOut: true,
            createdById: checkIn.createdById,
          },
        });
      })
    );

    return {
      success: `Auto checked out ${checkouts.length} order items at ${currentTime}`,
      checkouts,
    };
  } catch (error) {
    console.error("Auto checkout error:", error);
    return { error };
  }
};

export const unCheckInOrderItems = async (
  orderItemIds: string[] | "ALL",
  eventId: string,
  zoneId?: string | null,
  searchParams?: {
    zone?: string;
    search?: string;
    attendee?: string;
    group?: string;
  }
) => {
  try {
    let checksInOut;
    const zoneIdFilter = zoneId ?? undefined;
    let resolvedOrderItemIds = orderItemIds;
    if (orderItemIds === "ALL" && searchParams) {
      resolvedOrderItemIds = await resolveAttendeeReportOrderItemIds(
        eventId,
        searchParams
      );
    } else if (orderItemIds === "ALL") {
      const orderItems = await prisma.orderItem.findMany({
        where: {
          order: { eventId, status: OrderStatus.COMPLETED },
        },
      });
      resolvedOrderItemIds = orderItems.map((orderItem) => orderItem.id);
    }

    if (
      Array.isArray(resolvedOrderItemIds) &&
      resolvedOrderItemIds.length === 0
    ) {
      return { success: `Un checked in selected order items`, checksInOut: { count: 0 } };
    }

    if (resolvedOrderItemIds !== "ALL") {
      checksInOut = await prisma.checkInOut.deleteMany({
        where: {
          orderItemId: { in: resolvedOrderItemIds },
          ...(zoneIdFilter ? { zoneId: zoneIdFilter } : {}),
        },
      });
    }

    return { success: `Un checked in selected order items`, checksInOut };
  } catch (error) {
    return { error };
  }
};

export const getCheckInRulesByEventId = async (eventId: string) => {
  try {
    const checkInRules = await prisma.checkInRules.findMany({
      where: {
        AND: [
          { OR: [{ deletedAt: null }, { deletedAt: { isSet: false } }] },
          {
            OR: [
              {
                venue: {
                  eventIds: { has: eventId },
                },
              },
              {
                eventId,
              },
            ],
          },
        ],
      },
      include: {
        venue: {
          include: {
            translations: true,
          },
        },
        zones: {
          include: {
            translations: true,
          },
        },
      },
    });
    return checkInRules;
  } catch (error) {
    return { error };
  }
};

export const getCheckInRulesById = async (checkInRulesId: string) => {
  try {
    const checkInRules = await prisma.checkInRules.findUnique({
      where: { id: checkInRulesId },
      include: {
        venue: true,
        zones: true,
      },
    });
    return checkInRules;
  } catch (error) {
    return { error };
  }
};

export const createCheckInRules = async (data: CheckInRules) => {
  try {
    // First verify that the venue and zones exist
    if (!data.venueId) {
      return { error: { message: "Venue is required" } };
    }
    const venueId = data.venueId as string;
    const venue = await prisma.venue.findUnique({
      where: { id: venueId },
    });

    if (!venue) {
      return { error: { message: "Venue not found" } };
    }

    const zones = await prisma.zone.findMany({
      where: { id: { in: data.zonesIds } },
    });

    if (zones.length !== data.zonesIds.length) {
      return { error: { message: "One or more zones not found" } };
    }

    const checkInRules = await prisma.checkInRules.create({
      data: {
        name: data.name,
        venueId,
        numberOfScans: data.numberOfScans,
        minsBetweenScans: data.minsBetweenScans,
        isCheckoutRequired: data.isCheckoutRequired,
        validationStart: data.validationStart,
        validationEnd: data.validationEnd,
        autoCheckOut: data.autoCheckOut,
        zonesIds: data.zonesIds,
        deletedAt: null,
      },
      include: {
        venue: true,
        zones: true,
      },
    });

    // Bust the cached rules snapshot for every zone this rule attaches to
    // so the next scan picks up the new rule immediately (rather than
    // waiting up to CHECKIN_RULES_TTL_SECONDS). Best-effort: if Redis is
    // down the short TTL still self-heals within 10 minutes.
    await Promise.allSettled(
      (data.zonesIds ?? []).map((zoneId) =>
        deleteCache(checkinRulesKey(zoneId))
      )
    );

    return checkInRules;
  } catch (error) {
    console.error("Create check-in rules error:", error);
    return { error };
  }
};

export const updateCheckInRules = async (data: CheckInRules) => {
  try {
    // Capture the rule's CURRENT zone list before we mutate it so we
    // can also invalidate caches for zones being detached by this update
    // (otherwise their stale snapshot could persist for up to 10 min).
    const previous = await prisma.checkInRules.findUnique({
      where: { id: data.id },
      select: { zonesIds: true },
    });

    // disconnect all zones
    await prisma.checkInRules.update({
      where: { id: data.id },
      data: {
        zones: { disconnect: data.zonesIds.map((zoneId) => ({ id: zoneId })) },
      },
    });
    const checkInRules = await prisma.checkInRules.update({
      where: { id: data.id },
      data: {
        venueId: data.venueId,
        name: data.name,
        numberOfScans: data.numberOfScans,
        minsBetweenScans: data.minsBetweenScans,
        isCheckoutRequired: data.isCheckoutRequired,
        validationStart: data.validationStart,
        validationEnd: data.validationEnd,
        autoCheckOut: data.autoCheckOut,
        zones: {
          connect: data.zonesIds.map((zoneId) => ({ id: zoneId })),
        },
      },
    });

    // Invalidate the union of (previous zones, new zones) so a zone that
    // was just removed from the rule also drops its cached snapshot.
    const affectedZoneIds = new Set<string>([
      ...(previous?.zonesIds ?? []),
      ...(data.zonesIds ?? []),
    ]);
    await Promise.allSettled(
      Array.from(affectedZoneIds).map((zoneId) =>
        deleteCache(checkinRulesKey(zoneId))
      )
    );

    return checkInRules;
  } catch (error) {
    return { error };
  }
};

export const deleteCheckInRules = async (
  userId: string,
  checkInRulesId: string
) => {
  try {
    const checkInRules = await prisma.checkInRules.update({
      where: { id: checkInRulesId },
      data: {
        deletedAt: new Date(),
      },
      select: { id: true, zonesIds: true },
    });

    // After soft-delete, drop the cached rule snapshot for each zone
    // this rule was attached to. Without this, a scan within the next
    // 10 minutes could still enforce the deleted rule.
    await Promise.allSettled(
      (checkInRules.zonesIds ?? []).map((zoneId) =>
        deleteCache(checkinRulesKey(zoneId))
      )
    );

    return checkInRules;
  } catch (error) {
    return { error };
  }
};

export const getMaxNumberOfScans = async (zoneId: string) => {
  const zone = await prisma.zone.findUnique({
    where: { id: zoneId },
    select: {
      checkInRules: {
        select: {
          numberOfScans: true,
        },
      },
    },
  });

  const allowedScans = zone?.checkInRules ?? [];

  const validScans = allowedScans
    .map((rule) => rule.numberOfScans)
    .filter((n): n is number => typeof n === "number");

  const maxAllowedScans = Math.max(1, ...validScans);

  return maxAllowedScans;
};
